import { useEffect, useRef, useState } from "react";
import { UserPlus } from "lucide-react";
import { Input } from "../../components/Input";
import { api } from "../../api/client";
import type { UserPick } from "../../api/types";

// 出席者。**打字與選同仁兩條路並存**(業主 2026-09-19:
// 「出席者 可以直接選員工也可以自訂文字 最右邊一個+號選所有職員」)。
//
// 為什麼不做成純選單:會議常常有外部的人,而外部的人在系統裡沒有帳號。
// 為什麼不只留打字:自己人的名字每次都要重打,而且會打出三種寫法。

/** 名字之間用什麼分隔。中文的頓號 —— 業主原本就是這樣打的。 */
const SEP = "、";

/** 把一串名字拆開。空白與半形逗號也當分隔,因為人就是會那樣打。 */
function split(raw: string): string[] {
  return (raw || "")
    .split(/[、,,;;]+/)
    .map((one) => one.trim())
    .filter(Boolean);
}

export function AttendeesField({
  value,
  onChange,
}: {
  value: string;
  onChange: (next: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [staff, setStaff] = useState<UserPick[]>([]);
  const box = useRef<HTMLDivElement | null>(null);

  // 只在第一次打開時撈 —— 同仁名單在一次編輯裡不會變。
  useEffect(() => {
    if (!open || staff.length) return;
    let alive = true;
    void api
      .get<UserPick[]>("/users/pickable")
      .then((rows) => alive && setStaff(Array.isArray(rows) ? rows : []))
      .catch(() => alive && setStaff([]));
    return () => {
      alive = false;
    };
  }, [open, staff.length]);

  // 點外面就收起來。**沒有這一段的話,選單會一直跟著你**。
  useEffect(() => {
    if (!open) return;
    const away = (event: MouseEvent) => {
      if (box.current && !box.current.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", away);
    return () => document.removeEventListener("mousedown", away);
  }, [open]);

  const add = (name: string) => {
    const already = split(value);
    // **同一個人不會加兩次。** 加兩次的名單看起來像有人重複出席。
    if (!already.includes(name)) onChange([...already, name].join(SEP));
    setOpen(false);
  };

  return (
    <div ref={box} className="relative flex items-end gap-2">
      <div className="min-w-0 flex-1">
        <Input
          label="出席者"
          value={value}
          onChange={(event) => onChange(event.target.value)}
        />
      </div>
      <button
        type="button"
        aria-label="加入同仁"
        aria-expanded={open}
        onClick={() => setOpen((now) => !now)}
        className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[var(--radius)] border border-[var(--border-strong)] bg-[var(--surface)] text-[var(--text-2)] hover:bg-[var(--surface-2)]"
      >
        <UserPlus size={16} aria-hidden="true" />
      </button>

      {open ? (
        <ul
          role="listbox"
          aria-label="選同仁"
          className="absolute right-0 top-full z-20 m-0 mt-1 max-h-56 w-56 list-none overflow-y-auto rounded-[var(--radius)] border border-[var(--border-strong)] bg-[var(--surface)] p-0 shadow-lg"
        >
          {/* **`role="option"` 掛在真正可點的那一個元素上。**
              掛在 `<li>` 上的話,點擊落在沒有處理器的那一層 ——
              滑鼠使用者看起來按了卻沒反應。 */}
          {staff.map((one) => (
            <li key={one.id}>
              <button
                type="button"
                role="option"
                aria-selected={false}
                onClick={() => add(one.name)}
                className="w-full px-2.5 py-1.5 text-left text-sm text-[var(--text)] hover:bg-[var(--surface-2)]"
              >
                {one.name}
              </button>
            </li>
          ))}
          {staff.length === 0 ? (
            <li className="px-2.5 py-2 text-sm text-[var(--text-3)]">沒有同仁可以選</li>
          ) : null}
        </ul>
      ) : null}
    </div>
  );
}
